import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  UserCheck,
  CheckCircle,
  Shield,
  ShieldAlert
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import procurementApproverMasterService from '../../services/procurementApproverMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProcurementApproverMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalApprovers: 0,
    activeApprovers: 0,
    inactiveApprovers: 0,
    level1Approvers: 0,
    level2Approvers: 0,
    level3Approvers: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [approversResponse, statsResponse] = await Promise.all([
        procurementApproverMasterService.getAllProcurementApprovers(),
        procurementApproverMasterService.getProcurementApproverStats()
      ])
      
      if (approversResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (approversResponse.data || []).map(approver => ({
          ...approver,
          id: approver._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (approversResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No procurement approver records found. Create your first approver!')
        }
      } else {
        toast.error(approversResponse.message || 'Failed to load procurement approvers')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading procurement approver master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view procurement approvers')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load procurement approver master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getApprovalLevelBadgeClass = (level) => {
    const levelMap = {
      1: 'active',
      2: 'warning',
      3: 'pending',
      4: 'inactive',
      5: 'inactive'
    }
    return levelMap[level] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'approverCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'approverName',
      header: 'Approver Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'approver',
      header: 'User',
      cell: ({ row }) => {
        const approver = row.original.approver
        return (
          <div style={{ textAlign: 'left' }}>
            {approver ? `${approver.firstName || ''} ${approver.lastName || ''}`.trim() || approver.email : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'approvalLevel',
      header: 'Level',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getApprovalLevelBadgeClass(getValue())}`}>
            Level {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'approvalLimits.singleTransactionLimit',
      header: 'Single Transaction Limit',
      cell: ({ row }) => {
        const limit = row.original.approvalLimits?.singleTransactionLimit || 0
        const currency = row.original.approvalLimits?.singleTransactionCurrency || 'INR'
        return (
          <div style={{ textAlign: 'right', fontWeight: '600' }}>
            {limit > 0 ? new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(limit) : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'approvalLimits.monthlyLimit',
      header: 'Monthly Limit',
      cell: ({ row }) => {
        const limit = row.original.approvalLimits?.monthlyLimit || 0
        const currency = row.original.approvalLimits?.monthlyCurrency || 'INR'
        return (
          <div style={{ textAlign: 'right', fontWeight: '600' }}>
            {limit > 0 ? new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(limit) : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/procurement-approver-master/form')
  }

  const handleEdit = (approverData) => {
    navigate('/procurement/procurement-approver-master/edit', { state: { approverData } })
  }

  const handleView = (approverData) => {
    navigate('/procurement/procurement-approver-master/view', { state: { approverData } })
  }

  const handleDelete = (approverData) => {
    setDeleteItem(approverData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting procurement approver...')
      const response = await procurementApproverMasterService.deleteProcurementApprover(deleteItem._id)
      if (response.success) {
        toast.success('Procurement approver deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete procurement approver')
      }
    } catch (error) {
      console.error('Error deleting procurement approver:', error)
      if (error.response?.status === 404) {
        toast.error('Procurement approver not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Cannot delete approver with child approvers')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this procurement approver')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete procurement approver. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Masters', path: '/procurement?tab=masters' },
    { label: 'Approvers', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Approvers</h1>
          <p>Purchase approval hierarchy and limits</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Approver
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <UserCheck size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalApprovers || 0}</div>
          <div className="metric-title">Total Approvers</div>
          <div className="metric-subtitle">All approval roles</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeApprovers || 0}</div>
          <div className="metric-title">Active Approvers</div>
          <div className="metric-subtitle">Currently assigned</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Shield size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Level 1</span>
            </div>
          </div>
          <div className="metric-value">{stats.level1Approvers || 0}</div>
          <div className="metric-title">Level 1</div>
          <div className="metric-subtitle">First tier</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Shield size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Level 2</span>
            </div>
          </div>
          <div className="metric-value">{stats.level2Approvers || 0}</div>
          <div className="metric-title">Level 2</div>
          <div className="metric-subtitle">Second tier</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <ShieldAlert size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Level 3+</span>
            </div>
          </div>
          <div className="metric-value">{stats.level3Approvers || 0}</div>
          <div className="metric-title">Level 3+</div>
          <div className="metric-subtitle">Senior approval</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="procurement-approver-master-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Procurement Approver"
        message={`Are you sure you want to delete approver "${deleteItem?.approverName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProcurementApproverMaster

